<template>
  <button
    :class="buttonClassList"
    @click="onClick"
    :aria-checked="checked"
    :aria-label="`${displayName}, ${price}${checked ? ', selected' : ''}`"
    :disabled="alwaysSelected">
    <div class="absolute -top-1 -right-1 flex flex-col space-y-1 z-10">
      <Badge v-if="alwaysSelected" type="info" text="Always Included" />
      <Badge v-if="isPopular" type="accent" text="Popular" />
      <Badge v-if="isRecommended" type="success" text="Recommended" />
    </div>
    <div class="flex flex-col flex-grow space-y-2">
      <h4 class="text-base sm:text-lg font-extrabold leading-snug break-words hyphens-auto line-clamp-2">
        {{ displayName }}
      </h4>
      <p class="text-xs sm:text-sm leading-snug opacity-90">
        {{ rationale }}
      </p>
    </div>

    <!-- price -->
    <div class="mt-auto pt-2">
      <span class="block opacity-75 text-base sm:text-lg font-semibold leading-tight break-words">
        {{ format(price).formatted }} {{ $t(`plans.billingLabels.${billingLabel}`) }}
      </span>
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Badge from "@/components/ui/Badge.vue";
import { createCurrencyFormatter } from "@/utils/formatCurrency";
const format = createCurrencyFormatter();

const props = defineProps<{
  displayName: string;
  rationale: string;
  price: number;
  billingLabel: string;
  isPopular: boolean;
  alwaysSelected: boolean;
  isRecommended: boolean;
  checked: boolean;
}>();

const emit = defineEmits<{
  (e: "click"): void;
}>();

function onClick() {
  emit("click");
}

const buttonClassList = computed(() => {
  const base = [
    "btn",
    "flex flex-col items-start h-full p-4 sm:p-5 text-left relative w-full",
    "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary",
    "transition-all duration-300",
    "min-h-[140px] sm:min-h-[160px]",
  ].join(" ");

  if (props.alwaysSelected) {
    return [
      base,
      "btn-info",
      "border-info",
      "bg-info/10",
      "text-info-content",
      "shadow-md",
      "cursor-default",
      "scale-100",
      "hover:brightness-105",
    ];
  }

  const variant = props.checked ? "btn-primary scale-105 shadow-lg" : "btn-outline";

  return [base, variant];
});
</script>

<style scoped>
/* only needed if you haven’t enabled tailwind line-clamp plugin */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2; /* standard syntax */
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}
</style>
